import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { getUser, setToken, setUser } from "@/lib/storage";

const JOEY_SMILE = "https://customer-assets.emergentagent.com/job_648de01e-2b76-4a92-ab16-261bd9620d49/artifacts/tzi0vidg_3.png";

export const Landing = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (getUser()) navigate("/dashboard", { replace: true });
  }, [navigate]);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="brand-navy-band grain hero-band" style={{ paddingBottom: "5rem", paddingTop: "3.5rem" }}>
        <div className="container-wide hero-grid" style={{ display: "grid", gridTemplateColumns: "1fr 460px", gap: "3rem", alignItems: "center", color: "var(--warm-white)" }}>
          {/* Left column: pitch (splits into top + bottom on mobile so the auth panel can sit between) */}
          <div className="hero-pitch">
            <div className="hero-top">
              <p
                className="eyebrow fade-up"
                data-testid="landing-eyebrow"
                style={{ display: "inline-flex", alignItems: "baseline", gap: "0.7rem", flexWrap: "wrap", margin: 0, color: "rgba(242,233,219,0.85)" }}
              >
                <span
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 800,
                    fontSize: 26,
                    letterSpacing: "-0.01em",
                    color: "var(--warm-white)",
                    lineHeight: 1,
                    textTransform: "none",
                  }}
                >
                  Legally<span style={{ color: "var(--yellow)" }}>Legit</span>.ai
                </span>
                <span aria-hidden="true" style={{ display: "inline-block", width: 6, height: 6, background: "var(--yellow)", alignSelf: "center" }} />
                <span style={{ fontWeight: 700, letterSpacing: "0.1em", fontSize: 11, textTransform: "uppercase" }}>
                  Brand Protection Toolkit
                </span>
              </p>
              <h1
                className="fade-up delay-1 hero-headline"
                style={{ color: "var(--warm-white)", fontSize: "clamp(1.7rem, 5.5vw, 3.3rem)", lineHeight: 1.05, margin: "0.75rem 0 1rem", letterSpacing: "-0.02em" }}
                data-testid="landing-headline"
              >
                Let's make sure you legally own your brand, so you're never forced to rebrand.
              </h1>
            </div>

            <div className="hero-bottom fade-up delay-2" style={{ fontSize: "1.05rem", color: "var(--warm-white)", lineHeight: 1.6, maxWidth: 560 }}>
              <p style={{ margin: "0 0 0.85rem" }}>
                Most people don't find out their brand isn't legally protected until they get blind-sided by a cease-and-desist letter, or a competitor files for a trademark first, or they discover a copycat but can't do anything about it.
              </p>
              <p style={{ margin: "0 0 0.85rem", fontWeight: 700, color: "var(--yellow)" }}>
                But that won't be you.
              </p>
              <p style={{ margin: 0 }}>
                Inside, you'll find AI-powered tools built by a trademark lawyer that get to know your specific business, where you're most at risk of legal danger, and what to do next to stay safe. This way you can rest easy knowing no one can take away or copy what you're building.
              </p>
            </div>
          </div>

          {/* Right: auth panel */}
          <div className="auth-panel fade-up delay-3" style={{ background: "var(--paper)", color: "var(--midnight)", padding: "1.75rem 1.6rem", borderRadius: 4, border: "1px solid var(--hairline)", boxShadow: "8px 8px 0 0 var(--yellow)" }} data-testid="auth-panel">
            <AuthForm />
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            .hero-band {
              padding-top: 1.75rem !important;
              padding-bottom: 3rem !important;
            }
            .hero-grid {
              grid-template-columns: 1fr !important;
              gap: 1.25rem !important;
            }
            /* On mobile: let hero-pitch's children flow into the grid so we can interleave them with the auth panel */
            .hero-pitch {
              display: contents;
            }
            .hero-top    { order: 1; }
            .auth-panel  { order: 2; }
            .hero-bottom { order: 3; }
            .hero-headline {
              font-size: 1.7rem !important;
              line-height: 1.1 !important;
              margin-bottom: 0.4rem !important;
            }
          }
        `}</style>
      </section>

      {/* TOOLS */}
      <section style={{ padding: "4rem 0 3rem" }}>
        <div className="container-narrow">
          <p className="eyebrow">Inside the toolkit</p>
          <h2 style={{ fontSize: "clamp(1.8rem, 4vw, 2.4rem)", margin: "0.4rem 0 1rem", maxWidth: 720 }}>
            Built to give you answers, not anxiety.
          </h2>
          <p style={{ margin: "0 0 2rem", fontSize: "1.05rem", lineHeight: 1.6, color: "var(--midnight)", maxWidth: 720 }}>
            When you log into the Brand Protection Toolkit, you'll get a growing bundle of AI-powered tools — including:
          </p>

          <ul
            data-testid="landing-tool-list"
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
            }}
            className="landing-tool-list"
          >
            <ToolListItem
              tag="THE SNAPSHOT"
              title="The Legal IQ Assessment"
              body="Most entrepreneurs have no idea where their brand actually stands legally. They guess. They worry. They build for years on assumptions. This tool reads your business in a few minutes and hands you a clear Priority Map: the #1 thing to protect, the next thing to watch, and what you're already getting right."
              testId="landing-tool-item-quiz"
            />
            <ToolListItem
              tag="THE DEEP DIVE"
              title="Your Brand Protection Audit"
              body="A real brand protection review costs thousands, and most business owners can't afford one until something goes wrong. This tool gives you the same kind of read I'd give a paying client on a discovery call: where you're exposed, what's working, and the exact moves to make this month."
              testId="landing-tool-item-audit"
            />
            <ToolListItem
              tag="THE NAME CHECK"
              title="The Name Evaluator"
              body="Most people pick a business, course, or podcast name because the .com is available. Then they spend years building on a name that's weak, risky, or already taken. This tool tells you whether your name is the kind you can actually own and build on, before you spend another dollar on a logo or website."
              testId="landing-tool-item-name-test"
            />
            <ToolListItem
              tag="ASK ANYTHING"
              title="Ask Joey Anything"
              body="The questions you've been too embarrassed to ask a lawyer (or too nervous to Google) — naming, contracts, copycats, LLCs, cease-and-desist letters. Ask anything. You get plain-English answers from a real trademark lawyer's brain, no $400-an-hour clock running."
              testId="landing-tool-item-ask"
            />
          </ul>
          <style>{`
            .landing-tool-list > li:last-child { border-bottom: none !important; padding-bottom: 0 !important; }
          `}</style>
        </div>
      </section>

      {/* JOEY */}
      <section style={{ padding: "2rem 0 4rem" }}>
        <div className="container-wide credibility-row" style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: "2rem", alignItems: "flex-start" }}>
          <img
            src={JOEY_SMILE}
            alt="Joey Vitale"
            style={{ width: 180, height: 180, borderRadius: "50%", objectFit: "cover", border: "4px solid var(--navy)", boxShadow: "6px 6px 0 0 var(--yellow)" }}
            data-testid="landing-joey-photo"
          />
          <div>
            <p className="eyebrow">Built by a trademark lawyer</p>
            <div style={{ margin: "0.4rem 0 0", lineHeight: 1.65, fontSize: "1.02rem" }}>
              <p style={{ margin: "0 0 0.85rem" }}>
                I've seen too many entrepreneurs get blindsided by a cease-and-desist letter and forced to rebrand a business they spent years building. Almost every one of them told me the same thing: <em>"I never even thought to consider trademarks."</em>
              </p>
              <p style={{ margin: "0 0 0.85rem" }}>
                That's what I'm passionate about changing. Every entrepreneur building something real deserves to know their brand is theirs. Not "probably theirs." Not "I think we're fine." Actually theirs.
              </p>
              <p style={{ margin: "0 0 1rem" }}>
                Every tool here was custom-built by me, an actual trademark lawyer, so the answers hold up. Across my law firm Indie Law, my team has filed over 2,000 trademarks with a 99.7% success rate. That's the standard I built these tools to match.
              </p>
              <p style={{ margin: 0, fontWeight: 700, color: "var(--navy)" }}>
                — Joey Vitale, founder of Indie Law and author of <em>Legally Legit</em>
              </p>
            </div>
          </div>
        </div>
        <style>{`@media (max-width: 640px) { .credibility-row { grid-template-columns: 1fr !important; } }`}</style>
      </section>
    </div>
  );
};

const ToolListItem = ({ tag, title, body, testId }) => (
  <li
    data-testid={testId}
    style={{
      display: "grid",
      gridTemplateColumns: "auto 1fr",
      gap: "1.1rem",
      alignItems: "start",
      paddingBottom: "1.4rem",
      borderBottom: "1px dashed var(--hairline)",
    }}
  >
    <span
      aria-hidden="true"
      style={{
        width: 6,
        background: "var(--yellow)",
        alignSelf: "stretch",
        marginTop: 4,
        marginBottom: 4,
      }}
    />
    <div>
      <p className="eyebrow" style={{ margin: "0 0 0.25rem", color: "var(--cobalt)" }}>{tag}</p>
      <h3 style={{ margin: "0 0 0.45rem", fontSize: "1.2rem", color: "var(--navy)" }}>{title}</h3>
      <p style={{ margin: 0, color: "var(--midnight)", lineHeight: 1.6 }}>{body}</p>
    </div>
  </li>
);

// --- Auth form ---------------------------------------------------------------

const AuthForm = () => {
  const [mode, setMode] = useState("signup"); // default to create account
  return (
    <div>
      <div style={{ display: "inline-flex", background: "var(--warm-white-soft)", padding: 4, borderRadius: 3, border: "1px solid var(--hairline)", marginBottom: "1.25rem" }} data-testid="auth-mode-toggle">
        <TabBtn active={mode === "signup"} onClick={() => setMode("signup")} testId="auth-tab-signup">
          Create account
        </TabBtn>
        <TabBtn active={mode === "signin"} onClick={() => setMode("signin")} testId="auth-tab-signin">
          Sign in
        </TabBtn>
      </div>

      {mode === "signup" ? <SignupForm onSwitch={() => setMode("signin")} /> : <SigninForm onSwitch={() => setMode("signup")} />}
    </div>
  );
};

const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    style={{
      background: active ? "var(--navy)" : "transparent",
      color: active ? "var(--warm-white)" : "var(--midnight)",
      border: "none",
      padding: "0.55rem 1rem",
      borderRadius: 2,
      fontWeight: 700,
      fontSize: "0.85rem",
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      cursor: "pointer",
      fontFamily: "Arial, sans-serif",
      transition: "background-color 120ms ease, color 120ms ease",
    }}
  >
    {children}
  </button>
);

const SignupForm = ({ onSwitch }) => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim()) return toast.error("First name please.");
    if (!email.trim()) return toast.error("Email please.");
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    setSubmitting(true);
    try {
      const res = await api.post("/auth/register", {
        first_name: firstName.trim(),
        email: email.trim(),
        password,
      });
      setToken(res.data.token);
      setUser(res.data.user);
      toast.success(`You're in, ${res.data.user.first_name}.`);
      navigate("/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const msg = formatApiError(err?.response?.data?.detail) || "Couldn't create your account.";
      toast.error(msg);
      if (status === 409) onSwitch?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="signup-form" style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
      <h3 style={{ margin: "0 0 0.2rem", fontSize: "1.2rem" }}>Create your account.</h3>
      <p style={{ margin: "0 0 0.6rem", fontSize: "0.9rem", color: "var(--midnight)" }}>
        One email, one password. No payment, ever.
      </p>
      <input
        type="text"
        placeholder="First name"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        className="brand-input"
        data-testid="signup-first-name-input"
        autoComplete="given-name"
      />
      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="brand-input"
        data-testid="signup-email-input"
        autoComplete="email"
      />
      <input
        type="password"
        placeholder="Choose a password (8+ characters)"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="brand-input"
        data-testid="signup-password-input"
        autoComplete="new-password"
        minLength={8}
      />
      <button
        type="submit"
        className="btn-yellow"
        disabled={submitting}
        data-testid="signup-submit-button"
        style={{ marginTop: "0.3rem" }}
      >
        {submitting ? "Creating..." : "Create account & enter →"}
      </button>
      <p style={{ margin: "0.4rem 0 0", fontSize: "0.82rem", color: "var(--midnight)" }}>
        Already have an account?{" "}
        <button type="button" onClick={onSwitch} data-testid="signup-switch-to-signin" style={{ background: "transparent", border: "none", color: "var(--cobalt)", fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: "Arial, sans-serif", fontSize: "0.82rem" }}>
          Sign in →
        </button>
      </p>
    </form>
  );
};

const SigninForm = ({ onSwitch }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return toast.error("Email and password please.");
    setSubmitting(true);
    try {
      const res = await api.post("/auth/login", { email: email.trim(), password });
      setToken(res.data.token);
      setUser(res.data.user);
      toast.success(`Welcome back, ${res.data.user.first_name}.`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Sign in failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="signin-form" style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
      <h3 style={{ margin: "0 0 0.2rem", fontSize: "1.2rem" }}>Welcome back.</h3>
      <p style={{ margin: "0 0 0.6rem", fontSize: "0.9rem", color: "var(--midnight)" }}>
        Pick up where you left off.
      </p>
      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="brand-input"
        data-testid="signin-email-input"
        autoComplete="email"
      />
      <input
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="brand-input"
        data-testid="signin-password-input"
        autoComplete="current-password"
      />
      <button
        type="submit"
        className="btn-yellow"
        disabled={submitting}
        data-testid="signin-submit-button"
        style={{ marginTop: "0.3rem" }}
      >
        {submitting ? "Signing in..." : "Log me in →"}
      </button>
      <p style={{ margin: "0.4rem 0 0", fontSize: "0.82rem", color: "var(--midnight)" }}>
        Forgot your password?{" "}
        <a
          href="mailto:hello@gettrademarked.com?subject=Password%20reset%20request"
          data-testid="signin-forgot-link"
          style={{ color: "var(--cobalt)", fontWeight: 700 }}
        >
          Reset it →
        </a>
      </p>
    </form>
  );
};
